import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Mail, RefreshCw, Terminal, LogOut, CheckCircle, Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtTime = (iso) => {
  if (!iso) return 'Never';
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
    return d.toLocaleString();
  } catch { return iso; }
};

const CRMEmailSync = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncingId, setSyncingId] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [logs, setLogs] = useState([]);

  const addLog = (msg, type = 'info') => {
    const ts = new Date().toLocaleTimeString();
    setLogs(prev => [...prev.slice(-100), { ts, msg, type }]);
  };

  const loadSessions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/crm/sessions/list`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load');
      const data = await res.json();
      setSessions(data.sessions || []);
    } catch {
      toast.error('Failed to load Gmail accounts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  // Handle OAuth callback (?crm_session=...) — refresh sessions list
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('crm_session')) {
      loadSessions();
      toast.success('Gmail account connected');
    }
  }, [loadSessions]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res = await fetch(`${API}/api/oauth/gmail/login`, { credentials: 'include' });
      const data = await res.json();
      if (data.auth_url) {
        addLog('Opening Gmail authorization...', 'info');
        window.location.href = data.auth_url;
      }
    } catch {
      addLog('Failed to initiate Gmail connection', 'error');
      toast.error('Connection failed');
    } finally {
      setConnecting(false);
    }
  };

  const handleSync = async (session) => {
    setSyncingId(session.session_id);
    addLog(`Syncing ${session.user_email}...`, 'info');
    try {
      const res = await fetch(`${API}/api/emails/sync?session_id=${session.session_id}&max_results=100`, { credentials: 'include' });
      // Read body ONCE as text, then safely parse — eliminates "body stream already read" errors
      const raw = await res.text();
      let data = {};
      try { data = raw ? JSON.parse(raw) : {}; } catch { data = { detail: raw || 'Empty response' }; }

      if (!res.ok) {
        const detail = typeof data.detail === 'string' ? data.detail : (data.detail?.message || `HTTP ${res.status}`);
        if (res.status === 401) {
          addLog(`Session expired for ${session.user_email} — please reconnect`, 'error');
          toast.error(`Session expired for ${session.user_email}. Click Disconnect, then Add Another Gmail to reconnect.`);
        } else {
          addLog(`Error on ${session.user_email}: ${detail}`, 'error');
          toast.error(`${session.user_email}: ${detail}`);
        }
        return;
      }
      addLog(`${session.user_email}: ${data.emails_processed} emails → ${data.new_contacts} new contacts`, 'success');
      toast.success(`${session.user_email}: ${data.new_contacts} new contacts found`);
      await loadSessions();
    } catch (err) {
      addLog(`Sync failed for ${session.user_email}: ${err.message}`, 'error');
      toast.error(`Sync failed: ${err.message}`);
    } finally {
      setSyncingId(null);
    }
  };

  const handleSyncAll = async () => {
    for (const s of sessions) {
      // eslint-disable-next-line no-await-in-loop
      await handleSync(s);
    }
  };

  const handleDisconnect = async (session) => {
    if (!window.confirm(`Disconnect Gmail account ${session.user_email}? Auto-sync will stop for this account.`)) return;
    try {
      await fetch(`${API}/api/auth/crm-logout?session_id=${session.session_id}`, { method: 'POST', credentials: 'include' });
      toast.success(`Disconnected ${session.user_email}`);
      await loadSessions();
    } catch {
      toast.error('Disconnect failed');
    }
  };

  return (
    <div className="space-y-4">
      {/* Header bar */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-slate-800">Connected Gmail Accounts</p>
          <p className="text-xs text-gray-500">Each account auto-syncs every 4 hours. Add as many as you need.</p>
        </div>
        <div className="flex gap-2">
          {sessions.length > 0 && (
            <Button data-testid="sync-all-gmail-btn" onClick={handleSyncAll} disabled={!!syncingId} size="sm" variant="outline" className="border-indigo-600 text-indigo-700 hover:bg-indigo-50">
              {syncingId ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <RefreshCw size={14} className="mr-1.5" />} Sync All
            </Button>
          )}
          <Button data-testid="connect-gmail-btn" onClick={handleConnect} disabled={connecting} size="sm" className="bg-red-600 hover:bg-red-700">
            {connecting ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Plus size={14} className="mr-1.5" />}
            {sessions.length === 0 ? 'Connect Gmail' : 'Add Another Gmail'}
          </Button>
        </div>
      </div>

      {/* Account cards */}
      {loading ? (
        <div className="text-center py-8 text-gray-500"><Loader2 className="animate-spin inline mr-2" size={16} /> Loading accounts…</div>
      ) : sessions.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center">
            <Mail className="mx-auto text-gray-300 mb-2" size={32} />
            <p className="text-sm text-gray-500">No Gmail accounts connected yet.</p>
            <p className="text-xs text-gray-400 mt-1">Click "Connect Gmail" above to authorize an account.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="gmail-accounts-list">
          {sessions.map(s => (
            <Card key={s.session_id} className="border-emerald-200 bg-emerald-50/30" data-testid={`gmail-account-${s.session_id}`}>
              <CardContent className="py-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <CheckCircle className="text-emerald-600 shrink-0" size={18} />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">{s.user_email}</p>
                      <p className="text-xs text-gray-500">
                        Last sync: <span className="font-medium text-slate-700">{fmtTime(s.last_synced_at)}</span>
                        {s.last_sync_result?.status === 'ok' && s.last_sync_result?.new_contacts != null && (
                          <span className="ml-2 text-emerald-700">+{s.last_sync_result.new_contacts} new</span>
                        )}
                        {s.last_sync_result?.status === 'error' && (
                          <span className="ml-2 text-rose-700">Error: {s.last_sync_result.error?.slice(0, 60)}</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button
                      data-testid={`sync-gmail-${s.session_id}`}
                      onClick={() => handleSync(s)}
                      disabled={!!syncingId}
                      size="sm"
                      className="bg-indigo-600 hover:bg-indigo-700"
                    >
                      <RefreshCw size={14} className={`mr-1.5 ${syncingId === s.session_id ? 'animate-spin' : ''}`} />
                      {syncingId === s.session_id ? 'Syncing...' : 'Sync Now'}
                    </Button>
                    <Button
                      data-testid={`disconnect-gmail-${s.session_id}`}
                      onClick={() => handleDisconnect(s)}
                      variant="outline"
                      size="sm"
                      className="text-red-600 border-red-200 hover:bg-red-50"
                    >
                      <LogOut size={14} className="mr-1" /> Disconnect
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Sync Terminal */}
      <Card className="bg-gray-950 border-gray-800">
        <CardContent className="pt-4">
          <div className="flex items-center gap-2 mb-3 text-gray-400 text-xs">
            <Terminal size={14} /> <span>Email Sync Terminal</span>
          </div>
          <div className="font-mono text-xs space-y-1 min-h-[160px] max-h-[400px] overflow-y-auto" data-testid="sync-terminal">
            {logs.length === 0 ? (
              <p className="text-gray-600">{sessions.length === 0 ? 'Waiting for first Gmail connection...' : 'Click "Sync Now" on any account or "Sync All" to begin...'}</p>
            ) : (
              logs.map((l, i) => (
                <div key={i} className={`${l.type === 'error' ? 'text-red-400' : l.type === 'success' ? 'text-emerald-400' : 'text-gray-300'}`}>
                  <span className="text-gray-600">[{l.ts}]</span> {l.msg}
                </div>
              ))
            )}
            {syncingId && <div className="text-indigo-400 animate-pulse">Processing...</div>}
          </div>
        </CardContent>
      </Card>

      {/* Info */}
      <Card>
        <CardContent className="py-4">
          <p className="text-sm text-gray-500">
            <strong>How it works:</strong> Connect one or more Gmail accounts (work + personal). New contacts found in From/To headers are added to your CRM with source "email_sync". Existing contacts get their <code>email_count</code> incremented and <code>last_contacted</code> bumped.
          </p>
          <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
            <p className="text-sm text-emerald-800">
              <strong>Auto-sync enabled:</strong> All connected Gmail accounts are automatically synced every 4 hours. The LA County solicitations feed refreshes every 12 hours.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CRMEmailSync;
